import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { taskService } from '../../services/api';
import TaskCard from './TaskCard';
import './TaskList.css';

const TaskList = ({ userTasks = false }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
    status: userTasks ? '' : 'disponible',
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  });
  
  // Categorías para filtrar
  const categories = [
    'Todas',
    'Diseño',
    'Programación',
    'Marketing',
    'Traducción',
    'Administrativa',
    'Otra'
  ];
  
  // Cargar tareas
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setLoading(true);
        // Crear una copia de los filtros sin incluir 'Todas' como categoría
        const apiFilters = { ...filters };
        if (apiFilters.category === 'Todas' || apiFilters.category === '') {
          delete apiFilters.category;
        }
        
        // Si estamos viendo tareas del usuario, agregar filtro
        if (userTasks) {
          apiFilters.user = 'me'; // El backend interpretará 'me' como el usuario actual
        }
        
        const response = await taskService.getTasks(apiFilters);
        setTasks(response.data.tasks);
        setPagination(response.data.pagination);
        setError(null);
      } catch (err) {
        console.error('Error al cargar tareas:', err);
        setError('Error al cargar las tareas. Por favor, intenta de nuevo más tarde.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTasks();
  }, [filters, userTasks]);
  
  // Cambiar filtros
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value,
      // Resetear página cuando se cambia un filtro
      page: name !== 'page' ? 1 : value
    }));
  };
  
  // Cambiar página
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setFilters(prev => ({ ...prev, page: newPage }));
    }
  };
  
  return (
    <div className="task-list-container">
      <div className="row mb-4">
        <div className="col">
          <h1>{userTasks ? 'Mis Tareas' : 'Tareas Disponibles'}</h1>
          <p>
            {userTasks 
              ? 'Administra las tareas que has creado o aceptado.' 
              : 'Encuentra tareas que se ajusten a tus habilidades y comienza a ganar tokens BLUE.'}
          </p>
        </div>
        <div className="col-auto">
          <Link to="/tasks/create" className="btn btn-primary">
            <i className="fas fa-plus"></i> Crear Tarea
          </Link>
        </div>
      </div>
      
      <div className="filters-container mb-4">
        <div className="row g-3">
          <div className="col-md-3">
            <label htmlFor="category" className="form-label">Categoría</label>
            <select
              className="form-select"
              id="category"
              name="category"
              value={filters.category}
              onChange={handleFilterChange}
            >
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
          
          {userTasks && (
            <div className="col-md-3">
              <label htmlFor="status" className="form-label">Estado</label>
              <select
                className="form-select"
                id="status"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">Todos</option>
                <option value="disponible">Disponible</option>
                <option value="aceptada">En progreso</option>
                <option value="culminada">Completada</option>
                <option value="aprobada">Aprobada</option>
              </select>
            </div>
          )}
          
          <div className="col-md-3">
            <label htmlFor="sortBy" className="form-label">Ordenar por</label>
            <select
              className="form-select"
              id="sortBy"
              name="sortBy"
              value={filters.sortBy}
              onChange={handleFilterChange}
            >
              <option value="createdAt">Fecha</option>
              <option value="tokens">Pago</option>
            </select>
          </div>
          
          <div className="col-md-3">
            <label htmlFor="sortOrder" className="form-label">Orden</label>
            <select
              className="form-select"
              id="sortOrder"
              name="sortOrder"
              value={filters.sortOrder}
              onChange={handleFilterChange}
            >
              <option value="desc">Descendente</option>
              <option value="asc">Ascendente</option>
            </select>
          </div>
        </div>
      </div>
      
      {loading ? (
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-2">Cargando tareas...</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      ) : tasks.length === 0 ? (
        <div className="alert alert-info" role="alert">
          No hay tareas disponibles con los filtros seleccionados.
        </div>
      ) : (
        <>
          <div className="row row-cols-2 row-cols-sm-3 row-cols-md-4 row-cols-lg-6 g-2">
            {tasks.map(task => (
              <div className="col" key={task._id}>
                <TaskCard task={task} />
              </div>
            ))}
          </div>
          
          {/* Paginación */}
          {pagination.totalPages > 1 && (
            <nav className="mt-4">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => handlePageChange(pagination.page - 1)}
                  >
                    Anterior
                  </button>
                </li>
                
                {[...Array(pagination.totalPages).keys()].map(number => (
                  <li 
                    key={number + 1} 
                    className={`page-item ${pagination.page === number + 1 ? 'active' : ''}`}
                  >
                    <button 
                      className="page-link" 
                      onClick={() => handlePageChange(number + 1)}
                    >
                      {number + 1}
                    </button>
                  </li>
                ))}
                
                <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => handlePageChange(pagination.page + 1)}
                  >
                    Siguiente
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </>
      )}
    </div>
  );
};

export default TaskList;
